import {
  DetailType,
  ShotOffTargetAttribute,
} from '@collector/sportsapi-client-legacy'
import { ProbableIncident } from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'

import {
  AttributeConfig,
  DetailConfiguration,
  getAttributeId,
  OnSelectOptions,
} from '../../../../composables/useSportOptions'
import { hasIncidentSomeAttribute } from '../../../../Modals/utils'
import {
  onSelectTechniqueOfShot,
  onSelectTechniqueOfShotDetails,
} from '../../utils'
import { techniqueOfShotDetailsAttributes } from './techniqueOfShotDetailsAttributes'

export enum GroupName {
  Distance = 'Distance',
  ShotWoodwork = 'Shot woodwork',
  TechniqueOfShot = 'Technique of shot',
  TechniqueOfShotDetails = 'Technique of shot - details',
  PressureOfDefenders = 'Pressure of defenders',
  NumberOfDefendersOnTheLineOfShot = 'Number of defenders on the line of shot',
  AdditionalAttributes = 'Additional attributes',
}

export const groups: Map<
  GroupName,
  DetailConfiguration<ShotOffTargetAttribute>
> = new Map([
  [
    GroupName.Distance,
    {
      type: DetailType.Button,
      name: GroupName.Distance,
      important: true,
      attributes: [
        ShotOffTargetAttribute.InsidePenaltyArea,
        ShotOffTargetAttribute.OutsidePenaltyArea,
      ],
      cols: 2,
      multiple: false,
    },
  ],
  [
    GroupName.ShotWoodwork,
    {
      type: DetailType.Button,
      name: GroupName.ShotWoodwork,
      important: true,
      attributes: [
        ShotOffTargetAttribute.ShotWoodwork,
        {
          attributeId: ShotOffTargetAttribute.Crossbar,
          isActive: (attributes: ShotOffTargetAttribute[]) => {
            return attributes.includes(ShotOffTargetAttribute.ShotWoodwork)
          },
        } as AttributeConfig<ShotOffTargetAttribute>,
        {
          attributeId: ShotOffTargetAttribute.Post,
          isActive: (attributes: ShotOffTargetAttribute[]) => {
            return attributes.includes(ShotOffTargetAttribute.ShotWoodwork)
          },
        } as AttributeConfig<ShotOffTargetAttribute>,
      ],
      cols: 2,
      multiple: false,
      onSelect: (opts: OnSelectOptions<ShotOffTargetAttribute>) => {
        const {
          addAttribute,
          attributes,
          removeAttribute,
          selectedAttributes,
          toggleAttribute,
          attributeId,
        } = opts
        if (attributeId === ShotOffTargetAttribute.ShotWoodwork) {
          if (
            selectedAttributes.value.includes(
              ShotOffTargetAttribute.ShotWoodwork,
            )
          ) {
            attributes.forEach((attr) => removeAttribute(getAttributeId(attr)))
            return
          }
          addAttribute(ShotOffTargetAttribute.ShotWoodwork)
          return
        }

        if (attributeId === ShotOffTargetAttribute.Crossbar) {
          toggleAttribute(ShotOffTargetAttribute.Crossbar)
          removeAttribute(ShotOffTargetAttribute.Post)
          return
        }

        if (attributeId === ShotOffTargetAttribute.Post) {
          toggleAttribute(ShotOffTargetAttribute.Post)
          removeAttribute(ShotOffTargetAttribute.Crossbar)
          return
        }
      },
      fulfilledWhen: (
        _: ShotOffTargetAttribute[],
        probableIncident?: ProbableIncident,
      ) => {
        if (!probableIncident) return false

        if (
          !probableIncident.attribute_ids.includes(
            ShotOffTargetAttribute.ShotWoodwork,
          )
        )
          return true

        return [
          ShotOffTargetAttribute.Post,
          ShotOffTargetAttribute.Crossbar,
        ].some((attr) => probableIncident.attribute_ids.includes(attr))
      },
    },
  ],
  [
    GroupName.TechniqueOfShot,
    {
      type: DetailType.Button,
      name: GroupName.TechniqueOfShot,
      important: true,
      attributes: [
        ShotOffTargetAttribute.Normal,
        {
          attributeId: ShotOffTargetAttribute.FirstTouch,
          isActive: (attributes: ShotOffTargetAttribute[]) => {
            const validAttributes = [
              ShotOffTargetAttribute.Normal,
              ShotOffTargetAttribute.Volley,
            ]
            return attributes.some((attribute) =>
              validAttributes.includes(attribute),
            )
          },
        } as AttributeConfig<ShotOffTargetAttribute>,
        ShotOffTargetAttribute.Volley,
        ShotOffTargetAttribute.Head,
        ShotOffTargetAttribute.DirectFreeKick,
        ShotOffTargetAttribute.BicycleKick,
        ShotOffTargetAttribute.OtherBodyPart,
      ],
      cols: 3,
      multiple: false,
      onSelect: (opts: OnSelectOptions<ShotOffTargetAttribute>) =>
        onSelectTechniqueOfShot(opts, ShotOffTargetAttribute.FirstTouch),
    },
  ],
  [
    GroupName.TechniqueOfShotDetails,
    {
      type: DetailType.Button,
      name: GroupName.TechniqueOfShotDetails,
      important: true,
      attributes: techniqueOfShotDetailsAttributes,
      cols: 3,
      multiple: false,
      fulfilledWhen: (
        _attributes: ShotOffTargetAttribute[],
        probableIncident?: ProbableIncident,
      ) => {
        if (!probableIncident) return false

        const isFulfilled = probableIncident.attribute_ids.some(
          (attribute) =>
            attribute === ShotOffTargetAttribute.Head ||
            attribute === ShotOffTargetAttribute.OtherBodyPart,
        )

        if (isFulfilled) return true

        return hasIncidentSomeAttribute(
          probableIncident,
          techniqueOfShotDetailsAttributes.map((attribute) =>
            getAttributeId(attribute),
          ),
        )
      },
      onSelect: (opts: OnSelectOptions<ShotOffTargetAttribute>) =>
        onSelectTechniqueOfShotDetails(opts, [
          [ShotOffTargetAttribute.RightFoot, ShotOffTargetAttribute.LeftFoot],
          [ShotOffTargetAttribute.LeftFoot, ShotOffTargetAttribute.RightFoot],
          [ShotOffTargetAttribute.Chip],
        ]),
    },
  ],
  [
    GroupName.PressureOfDefenders,
    {
      type: DetailType.Button,
      name: GroupName.PressureOfDefenders,
      important: true,
      attributes: [
        ShotOffTargetAttribute.NoPressure,
        ShotOffTargetAttribute.LightPressure,
        ShotOffTargetAttribute.HighPressure,
      ],
      cols: 3,
      multiple: false,
    },
  ],
  [
    GroupName.NumberOfDefendersOnTheLineOfShot,
    {
      type: DetailType.Button,
      name: GroupName.NumberOfDefendersOnTheLineOfShot,
      important: true,
      attributes: [
        ShotOffTargetAttribute.ClearPosition,
        ShotOffTargetAttribute.OneDefender,
        ShotOffTargetAttribute.TwoOrMoreDefenders,
      ],
      cols: 3,
      multiple: false,
    },
  ],
  [
    GroupName.AdditionalAttributes,
    {
      type: DetailType.Button,
      name: GroupName.AdditionalAttributes,
      important: false,
      attributes: [
        ShotOffTargetAttribute.BigChanceMissed,
        ShotOffTargetAttribute.OneOnOne,
        ShotOffTargetAttribute.UncomfortablePosition,
        ShotOffTargetAttribute.EmptyNet,
      ],
      cols: 4,
      multiple: true,
    },
  ],
])
